import { getCoverage, getPatternGuide, isComplete } from './grid'
import type {
  CalculateSummaryOptions,
  GameSummary,
  LawnGrid,
  LawnStripeRank,
} from './types'

export const LAWN_STRIPE_CHALLENGE_PAR_SECONDS = 90
export const LAWN_STRIPE_MAX_TIME_PENALTY = 15

function clamp(value: number, minimum = 0, maximum = 100) {
  return Math.max(minimum, Math.min(maximum, value))
}

function round(value: number, decimals = 0) {
  const factor = 10 ** decimals
  return Math.round((value + Number.EPSILON) * factor) / factor
}

function axialDifference(a: number, b: number) {
  const difference = Math.abs((((a - b) % 180) + 180) % 180)
  return Math.min(difference, 180 - difference)
}

export function calculateStraightnessScore(grid: LawnGrid) {
  let total = 0
  let samples = 0

  for (const row of grid.cells) {
    for (const cell of row) {
      if (cell.kind !== 'grass' || cell.cutCount === 0 || !cell.cutDirection) continue
      const guide = getPatternGuide({
        pattern: grid.pattern,
        x: cell.x,
        y: cell.y,
        stripeWidthCells: grid.stripeWidthCells,
      })
      const directionScore = 1 - axialDifference(
        cell.cutDirection.angleDegrees,
        guide.angleDegrees,
      ) / 90
      const toneScore = cell.cutDirection.tone === guide.tone ? 1 : 0
      total += directionScore * 0.8 + toneScore * 0.2
      samples += 1
    }
  }

  return samples === 0 ? 0 : round(clamp((total / samples) * 100))
}

export function calculateEfficiencyScore(grid: LawnGrid) {
  const coverage = getCoverage(grid)
  if (coverage.cutCells === 0 || grid.metrics.grassContacts === 0) return 0

  const overlapRate = grid.metrics.overlapHits / grid.metrics.grassContacts
  const productiveScore = (1 - overlapRate) * 100
  const averageDeckWidth = grid.metrics.distanceCells > 0
    ? grid.metrics.deckWidthDistanceCells / grid.metrics.distanceCells
    : 1
  const idealDistance = Math.max(0, coverage.cutCells / averageDeckWidth - 1)
  const travelScore = grid.metrics.distanceCells <= idealDistance + 1e-9
    ? 100
    : idealDistance === 0
      ? 0
      : (idealDistance / grid.metrics.distanceCells) * 100

  return round(clamp(travelScore * 0.7 + productiveScore * 0.3))
}

export function calculateTurfHealthScore(grid: LawnGrid) {
  if (grid.metrics.grassContacts === 0) return 100

  const scalpRate = grid.metrics.scalpHits / grid.metrics.grassContacts
  const overlapRate = grid.metrics.overlapHits / grid.metrics.grassContacts
  const scalpPenalty = scalpRate * 70
  const overlapPenalty = overlapRate * 20
  const turnPenalty = Math.min(20, grid.metrics.sharpTurnEvents * 4)
  const obstaclePenalty = Math.min(10, grid.metrics.obstacleHits * 2)

  return round(clamp(100 - scalpPenalty - overlapPenalty - turnPenalty - obstaclePenalty))
}

export function calculateChallengeTimePenalty(durationSeconds: number) {
  if (!Number.isFinite(durationSeconds)) return 0
  const overtime = Math.max(0, durationSeconds - LAWN_STRIPE_CHALLENGE_PAR_SECONDS)
  const penalty = (overtime / LAWN_STRIPE_CHALLENGE_PAR_SECONDS) *
    LAWN_STRIPE_MAX_TIME_PENALTY
  return round(clamp(penalty, 0, LAWN_STRIPE_MAX_TIME_PENALTY))
}

export function getLawnStripeRank(score: number): LawnStripeRank {
  if (score >= 90) return 'Lawn Legend'
  if (score >= 80) return 'Stripe Artist'
  if (score >= 65) return 'Clean Cut'
  return 'Keep Mowing'
}

export function calculateSummary(
  grid: LawnGrid,
  options: CalculateSummaryOptions = {},
): GameSummary {
  const coverage = getCoverage(grid)
  const coverageScore = round(clamp(coverage.ratio * 100))
  const straightnessScore = calculateStraightnessScore(grid)
  const turfHealthScore = calculateTurfHealthScore(grid)
  const durationSeconds = Number.isFinite(options.durationSeconds)
    ? Math.max(0, options.durationSeconds ?? 0)
    : 0
  const timePenalty = grid.mode === 'challenge'
    ? calculateChallengeTimePenalty(durationSeconds)
    : 0
  const efficiencyScore = round(clamp(calculateEfficiencyScore(grid) - timePenalty))
  const overallScore = round(clamp(
    coverageScore * 0.25 +
    straightnessScore * 0.3 +
    efficiencyScore * 0.2 +
    turfHealthScore * 0.25,
  ))

  return {
    runId: grid.runId,
    mode: grid.mode,
    pattern: grid.pattern,
    durationSeconds: round(durationSeconds, 1),
    coveragePercent: coverage.percent,
    distanceCells: round(grid.metrics.distanceCells, 2),
    coverageScore,
    straightnessScore,
    efficiencyScore,
    turfHealthScore,
    overallScore,
    rank: getLawnStripeRank(overallScore),
    completed: isComplete(grid),
    cutCells: coverage.cutCells,
    totalCells: coverage.totalCells,
    overlapHits: grid.metrics.overlapHits,
    sharpTurnEvents: grid.metrics.sharpTurnEvents,
    scalpHits: grid.metrics.scalpHits,
    obstacleHits: grid.metrics.obstacleHits,
  }
}
